import { translate } from '~l10n/client';

import { AiUsageLimitEventAdapter } from '~analytics/adapters/AiUsageLimitEventAdapter';
import { toast } from '~shared/components/Toast';

import { isAxiosError } from '../../../../api/isAxiosError';

export function handleAiRateLimitError(error: unknown) {
  if (!isAxiosError(error)) {
    return;
  }

  if (
    error.response?.status === 429 &&
    isRateLimitError(error.response?.data)
  ) {
    const responseData = error.response.data;

    // Switches don't let you match on multiple properties :(
    // Makeshift pattern matching because we don't have an official version https://github.com/tc39/proposal-pattern-matching
    switch (true) {
      case isGlobalRateLimitError(responseData): {
        toast.critical(
          translate('Beta feature is at capacity. Please try again later.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'global_limit',
        }).sendAnalytics();

        break;
      }
      case isPerDayRateLimitExceeded(responseData): {
        toast.critical(
          translate('You’ve reached the daily limit for this beta.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'per_day',
        }).sendAnalytics();
        break;
      }

      case isMinuteRateLimitExceeded(responseData): {
        toast.critical(
          translate('Rate Limit Exceeded. Wait a few moments and try again.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'per_minute',
        }).sendAnalytics();
        break;
      }
    }
  }
}

type MinuteRateLimitExceeded = {
  error: 'rate_limit_exceeded';
};

type PerDayRateLimitExceeded = {
  error: 'rate_limit_exceeded';
  expiry: number;
};

type GlobalRateLimitExceeded = {
  error: 'global_rate_limit_exceeded';
};

type RateLimitError =
  | MinuteRateLimitExceeded
  | PerDayRateLimitExceeded
  | GlobalRateLimitExceeded;

function isRateLimitError(error: any): error is RateLimitError {
  return (error as RateLimitError).error !== undefined;
}

function isGlobalRateLimitError(
  response: RateLimitError
): response is GlobalRateLimitExceeded {
  return response.error === 'global_rate_limit_exceeded';
}

function isPerDayRateLimitExceeded(
  response: RateLimitError
): response is MinuteRateLimitExceeded {
  return (
    response.error === 'rate_limit_exceeded' &&
    (response as any).expiry !== undefined
  );
}

function isMinuteRateLimitExceeded(
  response: RateLimitError
): response is PerDayRateLimitExceeded {
  return (
    response.error === 'rate_limit_exceeded' &&
    (response as any).expiry === undefined
  );
}
